import BillingPage from "@components/billing";
import NavBar from "@components/NavBar";
import QueryFetchStatePage from "@components/QueryFetchStatePage";
import SubscriptionsSelectProvider from "@components/shared/SubscriptionsSelectProvider";
import { PlanProperties } from "@hooks/usePlans/types";
import { useQuery } from "@tanstack/react-query";
import { serverEndpoints } from "@utils/serverEndpoints";
import { authClient } from "@utils/authClient";
import { supabase } from "@utils/serverClient";
import { DateTime } from "luxon";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CustomOrg } from "types/index";
import { AddPaymentMethodButton, DraggableAlertUI } from "./components";
import {
  AddOn,
  DueSubscription,
  UserSubscription,
  UserSubscriptionsReturnProps,
} from "./types";
import { useDataQuery } from "@hooks/useData/useDataQuery";

const gracePeriod = 7;

const userSubscriptionsContext =
  createContext<UserSubscriptionsReturnProps | null>(null);

const { Provider } = userSubscriptionsContext;

type Props = {
  children: React.ReactNode;
  organization: CustomOrg;
};

export function UserSubscriptionsProvider({ children, organization }: Props) {
  const user = organization?.profile
  if (!user) return null;

  const serverUrl = serverEndpoints.userSubs;

  const [accountStatus, setAccountStatus] = useState<
    | "suspended"
    | "on_grace_period"
    | "on_grace_period_free_trial"
    | "nearing_grace_period"
    | "nearing_grace_period_free_trial"
    | "no_payment_method"
    | "no_payment_method_free_trial"
    | undefined
  >();

  const { status, data, refetch } = useDataQuery<{
    success: boolean;
    data: UserSubscription[];
  }>({
    queryKey: ["subscriptions", user.id, organization?.id],
    options: {
      gcTime: 1 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
    queryFn: async () => {
      const accessToken = await authClient.getAccessToken();

      if (!user.id || !accessToken) return { success: false, data: [] };

      const data = await fetch(serverUrl, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "user-id": user.id,
        },
      });
      return data.json();
    },
  });

  const subscriptions = data?.data?.filter(s => !s.deleted) ?? [];
  const subscription = useMemo(() => {
    return subscriptions.length === 0
      ? undefined
      : subscriptions.find(s => !s.deleted && s.businessId === organization?.id)
  }, [subscriptions])

  const { data: addOnsResponse } = useDataQuery<{
    success: boolean;
    data: AddOn;
  }>({
    queryKey: ["addOn"],
    options: {
      enabled: !!subscription?.id,
      gcTime: 1 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
    queryFn: async () => {
      if (!user?.id || !subscription) return { success: false, data: null };

      const accessToken = await authClient.getAccessToken();

      if (!accessToken) return { success: false, data: null };
      const data = await fetch(serverEndpoints.getAddOn(subscription!.id), {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const _json = await data.json();

      return _json;
    },
  });

  const planProperties = useMemo(() => {
    try {
      if (subscription) {
        const _planProps = JSON.parse(
          subscription?.plan.properties ?? "{}",
        ) as PlanProperties;
        const _addOnProps = JSON.parse(
          addOnsResponse?.data?.properties ?? "{}",
        ) as PlanProperties;

        return { ..._planProps, ..._addOnProps };
      } else {
        return {};
      }
    } catch (e) {
      return {};
    }
  }, [subscription, addOnsResponse]);

  const dueSubscriptions = useMemo(() => {
    const due: DueSubscription[] = [];

    (data?.data ?? []).forEach((sub) => {
      if (sub.deleted) return;
      sub?.subscriptionInvoices?.forEach((subInv) => {
        if (subInv.invoice.deleted) return;
        if (subInv.invoice.amount <= 0) return;
        const dueDate =
          subInv.invoice?.dueDate ||
          subInv.invoice?.current_billing_cycle_start;

        if (!dueDate) return;
        const dueDateTime = DateTime.fromISO(dueDate).toLocal();
        const now = DateTime.local();

        const daysLeft = dueDateTime.diff(now, "days").days;

        if (subInv.invoice.status === "unpaid" && daysLeft < gracePeriod) {
          due.push({
            sub: sub,
            daysLeft: daysLeft,
            id: sub.id,
            dueDate: dueDateTime,
          });
        }
      });
    });

    return due.sort((a, b) => {
      if (a.dueDate > b.dueDate) {
        return -1; // a is later than b
      } else if (a.dueDate < b.dueDate) {
        return 1; // a is earlier than b
      } else {
        return 0; // a and b are equal
      }
    });
  }, [data]);

  const hasPaymentMethod = subscription?.paymentMethod
  const isFreeTrial = planProperties.is_free_trial ?? false;
  const dueSubscriptionsWithinGracePeriod = useMemo(() => {
    return dueSubscriptions.filter(
      (e) => e.daysLeft >= -gracePeriod && e.daysLeft < 0,
    );
  }, [dueSubscriptions]);

  const dueSubscriptionsBeforeGracePeriod = useMemo(() => {
    return dueSubscriptions.filter(
      (e) => e.daysLeft <= gracePeriod && e.daysLeft > 0,
    );
  }, [dueSubscriptions]);

  const dueSubscriptionsPastGracePeriod = useMemo(() => {
    return dueSubscriptions
      .filter(
        sub =>
          // ignore subs until resumed
          !(sub.sub.status_date && new Date(sub.sub.status_date) > new Date())
      )
      .filter((e) => e.daysLeft < -gracePeriod);
  }, [dueSubscriptions]);

  useEffect(() => {
    if (dueSubscriptionsPastGracePeriod.length > 0) {
      setAccountStatus("suspended");
      return;
    } else if (dueSubscriptionsWithinGracePeriod.length) {
      setAccountStatus(
        organization?.plan_status === "free_trial"
          ? "on_grace_period_free_trial"
          : "on_grace_period",
      );
    } else if (dueSubscriptionsBeforeGracePeriod.length) {
      setAccountStatus(
        organization?.plan_status === "free_trial"
          ? "nearing_grace_period_free_trial"
          : "nearing_grace_period",
      );
    } else if (
      subscription?.paymentMethodId === null
    ) {
      setAccountStatus(
        organization?.plan_status === "free_trial"
          ? "no_payment_method_free_trial"
          : "no_payment_method",
      );
    } else {
      setAccountStatus(undefined);
    }
  }, [
    dueSubscriptionsWithinGracePeriod,
    dueSubscriptionsPastGracePeriod,
    dueSubscriptionsBeforeGracePeriod,
    subscription,
    organization,
  ]);

  function addPaymentButton({
    isOnFreeTrial = false,
  }: {
    isOnFreeTrial?: boolean;
  }) {
    return (
      <AddPaymentMethodButton
        subscription={subscription}
        isOnFreeTrial={isOnFreeTrial}
      />
    );
  }

  function getAlertInfo():
    | { title: string; description: string; trailing?: React.ReactNode }
    | undefined {
    switch (accountStatus) {
      case "suspended":
        // Account has been suspended
        // Show past invoices that need to cleared
        return {
          title: "Account suspension!",
          description: "Your account has been suspended.",
        };
      case "on_grace_period":
        return {
          title: "Account suspension warning!",
          description: `You have an invoice that is past due. Add a payment method before ${dueSubscriptionsWithinGracePeriod[0]?.dueDate.toLocaleString(
            DateTime.DATE_MED,
          )} to avoid account suspension.`,
          trailing: addPaymentButton({}),
        };

      case "on_grace_period_free_trial":
        if (hasPaymentMethod) return;
        if (!isFreeTrial) return;
        return {
          title: "Account suspension warning!",
          description: `You have an invoice that is past due. Add a payment method before ${dueSubscriptionsWithinGracePeriod[0]?.dueDate.toLocaleString(
            DateTime.DATE_MED,
          )} to avoid account suspension and extend your free trial by 2 months.`,
          trailing: addPaymentButton({
            isOnFreeTrial: true,
          }),
        };

      case "nearing_grace_period":
        if (hasPaymentMethod) return;
        return {
          title: "No payment method!",
          description: `You have a payment that is due soon, but no active payment method. Add a payment method by ${dueSubscriptionsBeforeGracePeriod[0]?.dueDate
            .plus({ days: gracePeriod })
            .toLocaleString(DateTime.DATE_MED)} to avoid account suspension.`,
          trailing: addPaymentButton({}),
        };

      case "nearing_grace_period_free_trial":
        if (hasPaymentMethod) return;
        if (!isFreeTrial) return;
        return {
          title: "No payment method!",
          description: `You have a payment that is due soon, but no active payment method. Add a payment method by ${dueSubscriptionsBeforeGracePeriod[0]?.dueDate
            .plus({ days: gracePeriod })
            .toLocaleString(
              DateTime.DATE_MED,
            )} to avoid account suspension and extend your free trial by 2 months.`,
          trailing: addPaymentButton({
            isOnFreeTrial: true,
          }),
        };

      case "no_payment_method":
        if (hasPaymentMethod) return;
        return {
          title: "No payment method!",
          description: "You have not added a payment method yet.",
          trailing: addPaymentButton({}),
        };

      case "no_payment_method_free_trial":
        if (hasPaymentMethod) return;
        if (!isFreeTrial) return;
        return {
          title: "No payment method!",
          description:
            "You have not added a payment method yet. Add a payment method to extend your free trial by 2 months.",
          trailing: addPaymentButton({
            isOnFreeTrial: true,
          }),
        };

      default:
        return undefined;
    }
  }

  const alertInfo = getAlertInfo();

  return (
    <Provider
      value={{
        subLength: subscriptions.length ?? 0,
        subscription,
        dueSubscriptions: dueSubscriptions ?? [],
        planProperties: planProperties,
        addOn: addOnsResponse?.data,
      }}
    >
      <div className="relative h-screen w-screen overflow-x-hidden">
        <QueryFetchStatePage onRetry={refetch} queryFetchStatus={status}>
          {data && status === "success" && (
            <>
              {accountStatus === "suspended" ? (
                <>
                  <NavBar />
                  <div className="mt-12 flex min-h-screen flex-col gap-2 p-4">
                    <h1 className="text-lg font-semibold">
                      Please clear your unpaid invoices before continuing.
                    </h1>
                    <hr />
                    <BillingPage />
                  </div>
                </>
              ) : subscriptions.length >= 1 &&
                dueSubscriptionsPastGracePeriod.length === 0 ? (
                children
              ) : (
                <SubscriptionsSelectProvider />
              )}
            </>
          )}
        </QueryFetchStatePage>

        {subscriptions.length > 0 && alertInfo && (
          <DraggableAlertUI alertInfo={alertInfo} />
        )}
      </div>
    </Provider>
  );
}

export const useUserSubscriptions: () => UserSubscriptionsReturnProps = () => {
  const context = useContext(userSubscriptionsContext);
  if (!context)
    throw "useUserSubscriptions must be used within a UserSubscriptionsProvider";
  return context;
};
